import type { Edge, Node } from '../../../core/models/scene';
import type { PersistedNode } from '../../compass/models/KnowledgeGraph';
import type { NetworkReviewItem, NetworkReviewSeverity } from './NetworkReviewItemService';

export interface NetworkReviewProjectionSceneOptions {
  readonly maxResources?: number;
  readonly maxReviewItems?: number;
  readonly origin?: { x: number; y: number };
}

export interface NetworkReviewProjectionScene {
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly hiddenResourceCount: number;
  readonly hiddenReviewItemCount: number;
}

const DEFAULT_ORIGIN = { x: 120, y: 120 };
const RESOURCE_WIDTH = 220;
const RESOURCE_HEIGHT = 86;
const REVIEW_WIDTH = 300;
const REVIEW_HEIGHT = 116;
const COLUMN_GAP = 180;
const RESOURCE_ROW_GAP = 28;
const REVIEW_ROW_GAP = 34;

export function buildNetworkReviewProjectionScene(
  resources: readonly PersistedNode[],
  reviewItems: readonly NetworkReviewItem[],
  options: NetworkReviewProjectionSceneOptions = {},
): NetworkReviewProjectionScene {
  const maxResources = options.maxResources ?? 18;
  const maxReviewItems = options.maxReviewItems ?? 10;
  const origin = options.origin ?? DEFAULT_ORIGIN;
  const visibleReviewItems = reviewItems.slice(0, maxReviewItems);
  const resourceIdsFromReviews = new Set(
    visibleReviewItems.flatMap((item) => item.resourceIds),
  );
  const visibleResources = [
    ...resources.filter((resource) => resourceIdsFromReviews.has(resource.id)),
    ...resources.filter((resource) => !resourceIdsFromReviews.has(resource.id)),
  ].slice(0, maxResources);
  const visibleResourceIds = new Set(visibleResources.map((resource) => resource.id));
  const resourceNodeIdById = new Map<string, string>();
  const reviewNodeIdById = new Map<string, string>();

  const resourceNodes = visibleResources.map((resource, index) => {
    const nodeId = sceneId('blueprint-resource', resource.id);
    resourceNodeIdById.set(resource.id, nodeId);
    const palette = resourcePalette(resource);
    return {
      id: nodeId,
      type: 'basic-shape',
      name: resourceLabel(resource),
      text: resourceText(resource),
      x: origin.x,
      y: origin.y + index * (RESOURCE_HEIGHT + RESOURCE_ROW_GAP),
      width: RESOURCE_WIDTH,
      height: RESOURCE_HEIGHT,
      w: RESOURCE_WIDTH,
      h: RESOURCE_HEIGHT,
      fillColor: palette.fillColor,
      strokeColor: palette.strokeColor,
      strokeWidth: 2,
      attrs: {
        variant: 'rectangle',
        label: resourceText(resource),
        fillColor: palette.fillColor,
        strokeColor: palette.strokeColor,
        textColor: '#0F172A',
        fontSize: 13,
        textAlign: 'left',
        verticalAlign: 'middle',
        blueprintProjection: {
          kind: 'resource',
          resourceId: resource.id,
          resourceType: resourceType(resource),
        },
      },
    } as Node;
  });

  const reviewX = origin.x + RESOURCE_WIDTH + COLUMN_GAP;
  const reviewNodes = visibleReviewItems.map((item, index) => {
    const nodeId = sceneId('blueprint-review', item.id);
    reviewNodeIdById.set(item.id, nodeId);
    const palette = severityPalette(item.severity);
    return {
      id: nodeId,
      type: 'basic-shape',
      name: item.title,
      text: `${severityLabel(item.severity)}: ${item.title}\n${item.summary}`,
      x: reviewX,
      y: origin.y + index * (REVIEW_HEIGHT + REVIEW_ROW_GAP),
      width: REVIEW_WIDTH,
      height: REVIEW_HEIGHT,
      w: REVIEW_WIDTH,
      h: REVIEW_HEIGHT,
      fillColor: palette.fillColor,
      strokeColor: palette.strokeColor,
      strokeWidth: 2,
      attrs: {
        variant: 'rectangle',
        label: `${severityLabel(item.severity)}: ${item.title}\n${item.summary}`,
        fillColor: palette.fillColor,
        strokeColor: palette.strokeColor,
        textColor: palette.textColor,
        fontSize: 12,
        textAlign: 'left',
        verticalAlign: 'middle',
        blueprintProjection: {
          kind: 'review_item',
          reviewItemId: item.id,
          severity: item.severity,
          angle: item.angle,
        },
      },
    } as Node;
  });

  const edges = visibleReviewItems.flatMap((item) => {
    const reviewNodeId = reviewNodeIdById.get(item.id);
    if (!reviewNodeId) return [];
    return item.resourceIds
      .filter((resourceId) => visibleResourceIds.has(resourceId))
      .map((resourceId) => {
        const resourceNodeId = resourceNodeIdById.get(resourceId);
        if (!resourceNodeId) return null;
        return {
          id: sceneId('blueprint-review-link', `${item.id}-${resourceId}`),
          type: 'line',
          from: reviewNodeId,
          to: resourceNodeId,
          fromAnchor: 'left',
          toAnchor: 'right',
          fromOffset: 0.5,
          toOffset: 0.5,
          fromAnchorLocked: true,
          toAnchorLocked: true,
          attrs: {
            label: severityLabel(item.severity),
            labelOffset: 0.5,
            routingType: 'bezier',
            color: severityPalette(item.severity).strokeColor,
            width: item.severity === 'critical' || item.severity === 'high' ? 3 : 2,
            startArrow: { type: 'none', size: 6 },
            endArrow: { type: 'chevron', size: 8 },
            blueprintProjection: {
              kind: 'review_to_resource',
              reviewItemId: item.id,
              resourceId,
            },
          },
        } as Edge;
      })
      .filter((edge): edge is Edge => edge !== null);
  });

  return {
    nodes: [...resourceNodes, ...reviewNodes],
    edges,
    hiddenResourceCount: Math.max(0, resources.length - visibleResources.length),
    hiddenReviewItemCount: Math.max(0, reviewItems.length - visibleReviewItems.length),
  };
}

function sceneId(prefix: string, value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 42) || 'item';
  return `${prefix}-${slug}-${hashString(value)}`;
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 2_147_483_647;
  }
  return Math.abs(hash).toString(36);
}

function resourceLabel(resource: PersistedNode): string {
  return resource.label || stringProp(resource.props, ['name', 'resourceName', 'resourceId']) || resource.id;
}

function resourceText(resource: PersistedNode): string {
  const label = resourceLabel(resource);
  const type = resourceType(resource);
  const owner = stringProp(resource.props, ['owner', 'team', 'costCenter']);
  return [label, type, owner ? `Owner: ${owner}` : null].filter(Boolean).join('\n');
}

function resourceType(resource: PersistedNode): string {
  return stringProp(resource.props, ['resourceType', 'managedService', '_manifestId', 'manifestId']) ?? resource.type;
}

function stringProp(props: unknown, keys: readonly string[]): string | null {
  if (!props || typeof props !== 'object' || Array.isArray(props)) return null;
  const record = props as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function resourcePalette(resource: PersistedNode): {
  fillColor: string;
  strokeColor: string;
} {
  switch (resource.type) {
    case 'data-store':
      return { fillColor: '#ECFEFF', strokeColor: '#0891B2' };
    case 'actor':
      return { fillColor: '#F5F3FF', strokeColor: '#7C3AED' };
    case 'policy':
      return { fillColor: '#FFF7ED', strokeColor: '#EA580C' };
    case 'resource-group':
      return { fillColor: '#F0FDF4', strokeColor: '#16A34A' };
    case 'place':
      return { fillColor: '#EFF6FF', strokeColor: '#2563EB' };
    case 'artifact':
    default:
      return { fillColor: '#F8FAFC', strokeColor: '#64748B' };
  }
}

function severityPalette(severity: NetworkReviewSeverity): {
  fillColor: string;
  strokeColor: string;
  textColor: string;
} {
  switch (severity) {
    case 'critical':
      return { fillColor: '#FEF2F2', strokeColor: '#DC2626', textColor: '#7F1D1D' };
    case 'high':
      return { fillColor: '#FFF7ED', strokeColor: '#EA580C', textColor: '#7C2D12' };
    case 'medium':
      return { fillColor: '#FEFCE8', strokeColor: '#CA8A04', textColor: '#713F12' };
    case 'low':
      return { fillColor: '#F0FDF4', strokeColor: '#16A34A', textColor: '#14532D' };
    case 'info':
    default:
      return { fillColor: '#EFF6FF', strokeColor: '#2563EB', textColor: '#1E3A8A' };
  }
}

function severityLabel(severity: NetworkReviewSeverity): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}
